/**
 * alert-display-log — dedicated logger for the alert-display pipeline.
 *
 * Every event flows to `xynth-frontend/frontend_alert_display.txt` via
 * /api/debug-log?channel=alert_display. The file is appended to; clear it
 * with `clearAlertDisplayLog()` or `curl -X DELETE http://localhost:3000/api/debug-log?channel=alert_display`.
 *
 * Instrumented call-sites (all on the alert canvas → modal display path):
 *   AlertCanvas.tsx          — active-run-details fetch / cell count / signature churn
 *   AlertCellNode.tsx        — mount, sortedOutputs summary, click-to-open
 *   AlertCellCarousel.tsx    — static-vs-rotate decision, which view is picked
 *   AlertCellOutputsView.tsx — tab list built, active tab changes
 *   DashboardView.tsx        — iframe mount, srcDoc size, height message
 *
 * Each entry looks like:
 *   { ts, server_ts, tag, cellIndex?, displayIndex?, sessionId?, ...payload }
 *
 * Tag convention: "<file>:<event>". Examples:
 *   "canvas:fetch_ok", "node:mount", "carousel:view_picked",
 *   "modal:tab_change", "dashboard:height"
 */

import { createChannelLogger } from "./debug-log";

export const alertLog = createChannelLogger("alert_display");

/**
 * Compact summary of a raw output suitable for logging (avoids dumping 50kb
 * of Plotly JSON into the log). Returns what KIND of output it is and a few
 * cheap size hints.
 */
export function summarizeOutput(o: unknown): Record<string, unknown> {
  if (!o || typeof o !== "object") return { kind: "none" };
  const out = o as { type?: string; data?: unknown };
  const type = out.type || "unknown";
  const data = out.data;

  if (typeof data === "string") {
    const lower = data.replace(/\s+/g, " ").toLowerCase();
    const isDashHtml =
      type === "html" && lower.includes('data-xynth-component="dashboard"');
    const isBacktestHtml =
      type === "html" && lower.includes('data-visualization-type="backtest-tabs"');
    const hasTable = type === "html" && /<table/i.test(data);
    return {
      kind: isDashHtml ? "dashboard_html" : type,
      bytes: data.length,
      html_table: hasTable || undefined,
      backtest_tabs: isBacktestHtml || undefined,
    };
  }
  if (type === "dashboard") {
    const bytes =
      typeof data === "string"
        ? (data as string).length
        : data
          ? JSON.stringify(data).length
          : 0;
    return { kind: "dashboard", bytes };
  }
  return { kind: type };
}

/** Clear the alert-display log file. */
export function clearAlertDisplayLog() {
  return fetch("/api/debug-log?channel=alert_display", { method: "DELETE" });
}
